import { getJawByToothNumber, makeOptionInitialValue } from 'lib/clinic/library';
import {
  ICreateOptionValue,
  IImplantFormat,
  IIndicationFormat,
  IIndicationGroup,
  IMaterialFormat,
  IOptionInfo,
  ISeqName,
  ITooth,
} from 'lib/types/clinic/projectTypes';
import { TValue } from 'lib/types/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallowAppSelector } from 'store/hooks';
import { useCreateProjectIndicationStore } from 'store/local/projectCreateindication';
import { useCreateProjectInfoStore } from 'store/local/projectCreateinfo';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { useDidUpdateEffect } from 'lib/utils';
import _ from 'lodash';

export const useCreateProjectOption = () => {
  // SECTION: declare server state
  const {
    indicationFormatData,
    indicationFormatSuccess,
    indicationInfoData,
    indicationInfoSuccess,
    indicationOptionData,
    indicationOptionOffline,
    indicationOptionOfflineSuccess,
  } = useShallowAppSelector(state => ({
    indicationFormatData: state.util.indicationFormat.data?.indicationFormat,
    indicationFormatSuccess: state.util.indicationFormat.success,
    indicationInfoData: state.util.indicationInfo.data?.indicationInfo,
    indicationInfoSuccess: state.util.indicationInfo.success,
    indicationOptionData: state.util.indicationOption.data,
    indicationOptionSuccess: state.util.indicationOption.success,
    indicationOptionOffline: state.util.indicationOptionOffline.data,
    indicationOptionOfflineSuccess: state.util.indicationOptionOffline.success,
  }));

  // SECTION: declare local state
  const {
    tooth,
    setTooth,
    teeth,
    setTeeth,
    optionTab,
    setOptionTab,
    isHasData,
    setIsHasData,
    // implantLibrary,
    // setImplantLibrary,
  } = useCreateProjectIndicationStore();
  const { isPhotogrammetry } = useProjectDetailStore();
  const { setIsLibraryOpen } = useCreateProjectInfoStore();

  // SECTION: declare variable
  const [checkEventQuery, setCheckEventQuery] = useState<any[]>([]);
  const indicationIdx = useMemo(() => {
    return !!tooth?.indicationIdx ? tooth?.indicationIdx : '';
  }, [tooth?.indicationIdx]);
  const materialIdx = useMemo(() => {
    return !!tooth?.material ? tooth?.material : '';
  }, [tooth?.material]);
  const implantType = useMemo(() => {
    return !!tooth?.implantType ? tooth?.implantType : 7;
  }, [tooth?.implantType]);
  const defaultOption = useMemo(() => {
    return !!tooth?.default ? tooth?.default : {};
  }, [tooth?.default]);
  const advancedOption = useMemo(() => {
    return !!tooth?.advanced ? tooth?.advanced : {};
  }, [tooth?.advanced]);
  const implantAbutmentMaterialIdx = useMemo(() => {
    if (!!tooth?.implantAbutmentMaterialIdx) {
      return tooth?.implantAbutmentMaterialIdx;
    } else {
      if (implantType === 8) {
        return 1;
      } else {
        return null;
      }
    }
  }, [tooth?.implantAbutmentMaterialIdx, implantType]);
  const implantScanbodyScanIdx = useMemo(() => {
    if (!!tooth?.implantScanbodyScanIdx) {
      return tooth?.implantScanbodyScanIdx;
    } else {
      if (implantType === 8 || implantType === 9) {
        return 1;
      } else {
        return null;
      }
    }
  }, [tooth?.implantScanbodyScanIdx, implantType]);
  const optionDefaultInfoList: IOptionInfo | null = useMemo(() => {
    return !!indicationOptionData?.default ? indicationOptionData?.default : null;
  }, [indicationOptionData]);
  const optionDefaultValues: ICreateOptionValue = useMemo(() => {
    return !!indicationOptionData?.default
      ? makeOptionInitialValue(indicationOptionData?.default)
      : {};
  }, [indicationOptionData]);
  const optionAdvancedInfoList: IOptionInfo | null = useMemo(() => {
    return !!indicationOptionData?.advanced ? indicationOptionData?.advanced : null;
  }, [indicationOptionData]);
  const optionAdvancedValues: ICreateOptionValue = useMemo(() => {
    return !!indicationOptionData?.advanced
      ? makeOptionInitialValue(indicationOptionData?.advanced)
      : {};
  }, [indicationOptionData]);
  const implantLibrary = useMemo(() => {
    return {
      implantLibraryIdx: tooth?.implantLibrary?.implantLibraryIdx || null,
      companyName: tooth?.implantLibrary?.companyName,
      companyKeyword: tooth?.implantLibrary?.companyKeyword,
      libraryDisplayName: tooth?.implantLibrary?.libraryDisplayName,
      libraryKeyword: tooth?.implantLibrary?.libraryKeyword,
      subtypeDisplayName: tooth?.implantLibrary?.subtypeDisplayName,
      subtypeKeyword: tooth?.implantLibrary?.subtypeKeyword,
      typeDisplayName: tooth?.implantLibrary?.typeDisplayName,
      typeKeyword: tooth?.implantLibrary?.typeKeyword,
    };
  }, [tooth?.implantLibrary]);

  const implantList: IImplantFormat[] | undefined = useMemo(() => {
    if (!indicationFormatData) return;

    const indicationFormat: IIndicationFormat[] = indicationFormatData.indication;

    return indicationFormat.reduce(
      (implantList: IImplantFormat[], indicationGroup: IIndicationFormat) => {
        const indication = indicationGroup.list.find(
          (indication: IIndicationGroup) => indication.seq === indicationIdx,
        );
        const material = indication?.materialList.find(
          (material: IMaterialFormat) => material.seq === materialIdx,
        );

        return material?.implantList ? implantList.concat(material?.implantList) : implantList;
      },
      [],
    );
  }, [indicationFormatData, indicationIdx, materialIdx]);

  const abutmentList: ISeqName[] | null | undefined = useMemo(() => {
    if (!implantList || !implantType) return;

    return implantList.find((implant: IImplantFormat) => implant.seq === implantType)
      ?.abutmentMaterialList;
  }, [implantList, implantType]);

  const scanbodyList: ISeqName[] | null | undefined = useMemo(() => {
    if (!implantList || !implantType) return;

    return implantList.find((implant: IImplantFormat) => implant.seq === implantType)
      ?.scanbodyScanList;
  }, [implantList, implantType]);

  const isSelectedTooth = useMemo(() => {
    return !!tooth?.number && !!indicationIdx && !!materialIdx;
  }, [!!tooth?.number, !!indicationIdx, !!materialIdx]);

  const isSituScanDisabled = useMemo(() => {
    if (!teeth) return;

    const upperJaw = teeth.filter(tooth => getJawByToothNumber(tooth.number) === 'upper') || [];
    const lowerJaw = teeth.filter(tooth => getJawByToothNumber(tooth.number) === 'lower') || [];

    return {
      upperJaw: upperJaw.length > 0,
      lowerJaw: lowerJaw.length > 0,
    };
  }, [teeth]);

  const pimType = useMemo(() => {
    return tooth?.pimType;
  }, [tooth?.pimType]);

  // SECTION: declare state
  // EFFECT: 포토그라메트리 라이브러리 초기화
  // useEffect(() => {
  //   if (isPhotogrammetry) {
  //     const implantLibrary = teeth[0]?.implantLibrary;

  //     if (!implantLibrary) return;

  //     setImplantLibrary({
  //       implantLibraryIdx: implantLibrary?.implantLibraryIdx || null,
  //       companyName: implantLibrary?.companyName,
  //       companyKeyword: implantLibrary?.companyKeyword,
  //       libraryDisplayName: implantLibrary?.libraryDisplayName,
  //       libraryKeyword: implantLibrary?.libraryKeyword,
  //       subtypeDisplayName: implantLibrary?.subtypeDisplayName,
  //       subtypeKeyword: implantLibrary?.subtypeKeyword,
  //       typeDisplayName: implantLibrary?.typeDisplayName,
  //       typeKeyword: implantLibrary?.typeKeyword,
  //     });
  //   }
  // }, [isPhotogrammetry]);

  // EFFECT: 포토그라메트리 상악, 하악 체크박스 초기화
  // useEffect(() => {
  //   if (!tooth?.default) return;

  //   if (isPhotogrammetry) {
  //     teeth.map(tooth => {
  //       if (getJawByToothNumber(tooth.number) === 'upper' && tooth.default?.situScan?.value === 1) {
  //         return {
  //           ...tooth,
  //           default: {
  //             ...tooth.default,
  //             ...{
  //               situScanUpper: {
  //                 value: 1,
  //                 isSavedLast: false,
  //               },
  //             },
  //           },
  //         };
  //       }
  //       if (getJawByToothNumber(tooth.number) === 'lower' && tooth.default?.situScan?.value === 1) {
  //         return {
  //           ...tooth,
  //           default: {
  //             ...tooth.default,
  //             ...{
  //               situScanLower: {
  //                 value: 1,
  //                 isSavedLast: false,
  //               },
  //             },
  //           },
  //         };
  //       }
  //     });

  //     setTooth(prev => {
  //       if (!prev) return null;

  //       if (getJawByToothNumber(prev?.number) === 'upper') {
  //         return {
  //           ...prev,
  //           default: {
  //             ...prev?.default,
  //             ...{
  //               situScanUpper: {
  //                 value: 1,
  //                 isSavedLast: false,
  //               },
  //             },
  //           },
  //         };
  //       }
  //       if (getJawByToothNumber(prev?.number) === 'lower') {
  //         return {
  //           ...prev,
  //           default: {
  //             ...prev?.default,
  //             ...{
  //               situScanLower: {
  //                 value: 1,
  //                 isSavedLast: false,
  //               },
  //             },
  //           },
  //         };
  //       }

  //       return prev;
  //     });
  //   }
  // }, [isPhotogrammetry]);

  // EFFECT: 기본 옵션 초기화 - 인디케이션 선택할때마다 동작
  useEffect(() => {
    if (isPhotogrammetry) return;
    if (isHasData) return;

    setTooth((prev: ITooth | null) => ({
      ...prev,
      default: optionDefaultValues,
    }));
  }, [isPhotogrammetry, optionDefaultValues]);

  // EFFECT: 포토그라메트리 옵션 초기화 - PIM 치아 추가할때마다 동작
  useEffect(() => {
    if (!isPhotogrammetry) return;
    if (isHasData) return;

    setTooth((prev: ITooth | null) => ({
      ...prev,
      default: {
        ...optionDefaultValues,
        situScan: { value: prev?.pimType === 'normal' ? 1 : 0, isSavedLast: false },
      },
    }));

    setTeeth(prev =>
      prev.map(item => {
        return {
          ...item,
          default: {
            ...optionDefaultValues,
            situScan: { value: item?.pimType === 'normal' ? 1 : 0, isSavedLast: false },
          },
        };
      }),
    );
  }, [isPhotogrammetry, optionDefaultValues, teeth?.length]);

  // EFFECT: 고급옵션 초기화 - 인디케이션 선택할때마다 동작
  useEffect(() => {
    if (isPhotogrammetry) return;
    if (isHasData) return;

    setTooth((prev: ITooth | null) => ({
      ...prev,
      advanced: optionAdvancedValues,
    }));
  }, [isPhotogrammetry, optionAdvancedValues]);

  // EFFECT: 포토그라메트리 고급옵션 초기화 - PIM 치아 추가할때마다 동작
  useEffect(() => {
    if (!isPhotogrammetry) return;
    if (isHasData) return;

    setTooth((prev: ITooth | null) => ({
      ...prev,
      advanced: optionAdvancedValues,
    }));

    setTeeth(prev =>
      prev.map(item => {
        return { ...item, advanced: optionAdvancedValues };
      }),
    );
  }, [isPhotogrammetry, optionAdvancedValues, teeth?.length]);

  // EFFECT: 포토그라메트리 상악/하악 옵션값 일괄 변경
  useDidUpdateEffect(() => {
    if (checkEventQuery[0] === 'situScanUpper' || checkEventQuery[0] === 'situScanLower') {
      setTeeth(prev =>
        prev.map(item => {
          if (getJawByToothNumber(item.number) === 'upper') {
            const newValue = {
              situScan: {
                isSavedLast: !!item.default?.['situScan']?.isSavedLast,
                value: item?.default?.['situScanUpper']?.value || 0,
              },
            };

            return { ...item, default: { ...item.default, ...newValue } };
          } else if (getJawByToothNumber(item.number) === 'lower') {
            const newValue = {
              situScan: {
                isSavedLast: !!item.default?.['situScan']?.isSavedLast,
                value: item?.default?.['situScanLower']?.value || 0,
              },
            };

            return { ...item, default: { ...item.default, ...newValue } };
          } else {
            return { ...item };
          }
        }),
      );
    }
  }, [checkEventQuery]);

  // SECTION: declare function
  const handleTabChange = (index: number) => {
    setOptionTab(index);
  };

  const handleCheckChange = useCallback(
    (type: 'default' | 'advanced', optionKey: string, value: TValue, isPhotogrammetry: boolean) => {
      setIsHasData(true);
      setCheckEventQuery([optionKey, value]);

      const option = type === 'default' ? tooth?.default || {} : tooth?.advanced || {};

      const newValue = {
        [optionKey]: {
          isSavedLast: !!option[optionKey]?.isSavedLast,
          value: value ? 0 : 1,
        },
      };

      if (type === 'default') {
        setTooth((state: ITooth | null) => ({ ...state, default: { ...option, ...newValue } }));
        if (isPhotogrammetry) {
          setTeeth(prev =>
            prev.map(item => {
              return { ...item, default: { ...option, ...newValue } };
            }),
          );
        }
      } else {
        setTooth((state: ITooth | null) => ({ ...state, advanced: { ...option, ...newValue } }));
        if (isPhotogrammetry) {
          setTeeth(prev =>
            prev.map(item => {
              return { ...item, advanced: { ...option, ...newValue } };
            }),
          );
        }
      }
    },
    [tooth, isPhotogrammetry],
  );

  const handleOptionChange = (type: 'default' | 'advanced', optionKey: string, value: TValue) => {
    setIsHasData(true);

    const option = type === 'default' ? tooth?.default || {} : tooth?.advanced || {};

    const newValue = {
      [optionKey]: {
        isSavedLast: !!option[optionKey]?.isSavedLast,
        value: value,
      },
    };

    if (type === 'default') {
      setTooth((state: ITooth | null) => ({ ...state, default: { ...option, ...newValue } }));
      if (isPhotogrammetry) {
        setTeeth(prev =>
          prev.map(item => {
            return { ...item, default: { ...option, ...newValue } };
          }),
        );
      }
    } else {
      setTooth((state: ITooth | null) => ({ ...state, advanced: { ...option, ...newValue } }));
      if (isPhotogrammetry) {
        setTeeth(prev =>
          prev.map(item => {
            return { ...item, advanced: { ...option, ...newValue } };
          }),
        );
      }
    }
  };

  const handleImplantChange = (value: TValue) => {
    setIsHasData(true);

    const implantType: number = Number(value);
    let implantAbutmentMaterialIdx: number | null = null;
    let implantScanbodyScanIdx: number | null = null;

    if (implantType === 8) {
      implantAbutmentMaterialIdx = 1;
      implantScanbodyScanIdx = 1;
    } else if (implantType === 9) {
      implantAbutmentMaterialIdx = null;
      implantScanbodyScanIdx = 1;
    } else {
      implantAbutmentMaterialIdx = null;
      implantScanbodyScanIdx = null;
    }

    setTooth((state: ITooth | null) => ({
      ...state,
      implantType: implantType,
      implantAbutmentMaterialIdx: implantAbutmentMaterialIdx,
      implantScanbodyScanIdx: implantScanbodyScanIdx,
    }));
    if (isPhotogrammetry) {
      setTeeth(prev =>
        prev.map(item => {
          if (item.pimType === tooth?.pimType) {
            return {
              ...item,
              implantType: implantType,
              implantAbutmentMaterialIdx: implantAbutmentMaterialIdx,
              implantScanbodyScanIdx: implantScanbodyScanIdx,
            };
          } else {
            return item;
          }
        }),
      );
    }
  };

  const handleAbutmentMaterialChange = (value: TValue) => {
    setIsHasData(true);

    setTooth((state: ITooth | null) => ({ ...state, implantAbutmentMaterialIdx: Number(value) }));
    if (isPhotogrammetry) {
      setTeeth(prev =>
        prev.map(item => {
          return {
            ...item,
            implantAbutmentMaterialIdx: Number(value),
          };
        }),
      );
    }
  };

  const handleScanbodyScanChange = (value: TValue) => {
    setIsHasData(true);

    setTooth((state: ITooth | null) => ({ ...state, implantScanbodyScanIdx: Number(value) }));
    if (isPhotogrammetry) {
      setTeeth(prev =>
        prev.map(item => {
          return {
            ...item,
            implantScanbodyScanIdx: Number(value),
          };
        }),
      );
    }
  };

  const handleLibraryClick = () => {
    setIsLibraryOpen(true);
  };

  const handleDeleteItemClick = () => {
    // setImplantLibrary(null);
    setTooth(prev => {
      return { ...prev, implantLibrary: undefined };
    });
    setTeeth(prev =>
      prev.map(prevItem => {
        if (prevItem.number === tooth?.number) {
          return { ...prevItem, implantLibrary: undefined };
        } else {
          return prevItem;
        }
      }),
    );
  };

  return {
    optionTab,
    implantType,
    implantList,
    implantAbutmentMaterialIdx,
    abutmentList,
    implantScanbodyScanIdx,
    scanbodyList,
    defaultOption,
    optionDefaultInfoList,
    advancedOption,
    optionAdvancedInfoList,
    optionAdvancedValues,
    isSelectedTooth,
    isPhotogrammetry,
    implantLibrary,
    isSituScanDisabled,
    pimType,
    onTabChange: handleTabChange,
    onCheckChange: handleCheckChange,
    onOptionChange: handleOptionChange,
    onImplantChange: handleImplantChange,
    onAbutmentMaterialChange: handleAbutmentMaterialChange,
    onScanbodyScanChange: handleScanbodyScanChange,
    onLibraryClick: handleLibraryClick,
    onDeleteItemClick: handleDeleteItemClick,
  };
};
